import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { generatePath } from 'react-router';
import { PATHS } from 'routes/constants';
import { saveUrl } from 'redux/reducers/auth/reducer';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { searchCreditNotesRequest } from 'redux/reducers/invoiceManagement/reducer';
import {
	selectCreditNotes,
	selectCreditNotesLoading,
} from 'redux/reducers/invoiceManagement/selectors';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { getEMerchant } from 'redux/reducers/monthlyFee/selectors';
import { TCreditNoteRow, tableStructure as defaultTableStructure } from './tableStructure';

/** Distinct values of the credited invoices, e.g. "21%, 9%". */
const joinDistinct = (values: (string | number | null | undefined)[]): string =>
	Array.from(new Set(values.filter((value) => value != null).map(String))).join(', ');

export const CreditNotes: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const list = useSelector(selectCreditNotes);
	const listLoading = useSelector(selectCreditNotesLoading);
	const emList = useSelector(getEMerchant);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TCreditNoteRow>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchCreditNotesRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	/** EM names come from the EM list, the credit note carries only the EM id. */
	useEffect(() => {
		if (!emList) dispatch(getEMerchantRequest());
	}, [dispatch, emList]);

	const items = useMemo(() => {
		if (!list) return null;
		const emNames = new Map(emList?.map((em) => [em.id, em.fullName]));
		return {
			...list,
			data: (list.data ?? []).map(
				(item): TCreditNoteRow => ({
					...item,
					emName: emNames.get(item.emId) ?? String(item.emId),
					invoiceNumber: joinDistinct(
						(item.invoices ?? []).map((invoice) => invoice.invoiceNumber),
					),
				}),
			),
		};
	}, [list, emList]);

	const handleAction = useCallback(() => {}, []);

	const handleLink = useCallback(
		(creditNoteId: number) => {
			dispatch(saveUrl(window.location.pathname + window.location.search));
			navigate(
				generatePath(PATHS.INVOICE_MANAGEMENT_CREDIT_NOTE_DETAILS_PAGE, {
					creditNoteId: String(creditNoteId),
				}),
			);
		},
		[dispatch, navigate],
	);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TCreditNoteRow>
				tableStructure={currentTableStructure}
				items={items}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				handleLink={handleLink}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Invoice Management / <b>Credit Notes</b>
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
